import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import React, { useState } from 'react';
import { useForm } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { VenueSelector, VenueSelectorForm } from 'modules/venue-selector';

import { VenueRowItem } from './VenueRowItem';
import { VenuesList } from './VenuesList';
import { ReportVenueSelectItem } from './types';
import ReportFormItem from '../../core/ReportFormItem';
import { InsightReportForm } from '../../types';

type VenueFormItemProps = {
  initialValues: VenueSelectorForm;
  venues: Venue[];
  disabled?: boolean;
  onUpdate(venueIds: string[]): void;
};

const ReportFormItemValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const VenuesFormItem = ({ initialValues, venues, onUpdate, disabled }: VenueFormItemProps) => {
  const { formatMessage } = useIntl();
  const insightsReportForm = useForm<InsightReportForm>();
  const insightsReportFormState = insightsReportForm.getState();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { venueIds } = initialValues;
  const venueSelectItems: ReportVenueSelectItem[] = venues.map((venue: Venue) => ({
    id: venue.id,
    name: venue.name,
    address: venue.address,
    city: venue.city,
    post_code: venue.post_code,
    is_wolt_plus: venue.is_wolt_plus,
    picker_api_enabled: venue.picker_api_enabled,
  }));

  const selectedVenueItems = venueSelectItems.filter(venue => venueIds.includes(venue.id));
  return (
    <ReportFormItem
      title={<FormattedMessage id="insights.reports.venues.question" />}
      value={
        <ReportFormItemValue>
          <VenueSelector<ReportVenueSelectItem>
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            selectedVenueIds={venueIds}
            onUpdate={onUpdate}
            venueSelectItems={venueSelectItems}
            rowRenderer={VenueRowItem}
          />
          <VenuesList venueSelectItems={selectedVenueItems} onViewAll={() => setIsModalOpen(true)} />
          {insightsReportFormState.submitFailed && insightsReportFormState.errors?.venueIds && (
            <Alert variant="error" size="small">
              {formatMessage({ id: insightsReportFormState.errors.venueIds.id })}
            </Alert>
          )}
        </ReportFormItemValue>
      }
      action={
        disabled ? null : (
          <Button variant="secondary" size="small" onClick={() => setIsModalOpen(true)}>
            <FormattedMessage
              id={venueIds.length > 0 ? 'insights.reports.venues.change-venues' : 'insights.reports.venues.add-venues'}
            />
          </Button>
        )
      }
    />
  );
};
